import { Button } from "@/components/ui/Button";
import { BodyText } from "@/components/ui/Text";
import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import {
  type ChatMessage,
  TranslationState,
} from "../../types/chatMessageTypes";
import { MessageBubble } from "./MessageBubble";

interface TextMessageProps {
  message: ChatMessage;
}

export const TextMessage = ({ message }: TextMessageProps) => {
  const [showTranslation, setShowTranslation] = useState(
    message.tag === "CUSTOMER" &&
      message.translationState === TranslationState.COMPLETE,
  );

  const originalText = message.text || message.message || "";
  const translatedText = message.translations?.es || message.translations?.en;

  // useState initializer only runs on mount, so if a message arrives as PENDING
  // and later transitions to COMPLETE, we need to auto-switch to the translation.
  // translatedText (a string primitive) is used instead of message.translations
  // (an object) to avoid re-triggering on every parent re-render, which would
  // override the user's manual "Show original" choice.
  useEffect(() => {
    if (
      message.tag === "CUSTOMER" &&
      message.translationState === TranslationState.COMPLETE &&
      translatedText
    ) {
      setShowTranslation(true);
    }
  }, [message.tag, message.translationState, translatedText]);
  const isAsync = message.modelType === "AsyncMessage";
  const hasTranslation =
    message.translationState === TranslationState.COMPLETE && translatedText;

  // Determine which text to show as primary content
  const primaryContent = showTranslation ? translatedText : originalText;

  const handleToggleTranslation = () => {
    setShowTranslation(!showTranslation);
  };

  const getTranslationFooter = () => {
    if (!message.translationState) return null;

    switch (message.translationState) {
      case TranslationState.COMPLETE:
        return hasTranslation ? (
          <Button
            variant="link"
            size="xs"
            className="self-start"
            onClick={handleToggleTranslation}
          >
            {showTranslation ? "Show original" : "Show translation"}
          </Button>
        ) : null;
      case TranslationState.PENDING:
        return (
          <BodyText
            size="xs"
            textColor="tertiary"
            className="flex items-center gap-1"
          >
            <LoaderCircle className="w-3 h-3 animate-spin" />
            Translating
          </BodyText>
        );
      case TranslationState.ERROR:
        return (
          <BodyText size="xs" textColor="error">
            Translation failed and will automatically retry. Patient responses
            may not be translated.
          </BodyText>
        );
      default:
        return null;
    }
  };

  return (
    <MessageBubble
      timestamp={message.created}
      key={message.uuid}
      variant={isAsync ? "async" : "default"}
      footer={getTranslationFooter()}
    >
      <BodyText size="sm" preserveLineBreaks>
        {primaryContent}
      </BodyText>
    </MessageBubble>
  );
};
